/**
 * `usePlayer` — the identity, as React state (§ F117).
 *
 * A hook rather than a context on purpose: the store is a module (`player.ts`)
 * and the subscription is a browser event, so any component anywhere can read
 * it without a provider being threaded through four page trees that do not
 * otherwise share one. `useSyncExternalStore` is what keeps every consumer in
 * step within a single commit.
 */

import { useCallback, useSyncExternalStore } from "react";
import {
  loadAccounts,
  loadPlayer,
  migrateLegacyPlayer,
  savePlayer,
  subscribePlayer,
  type StoredPlayer,
} from "./player";

// Once, at import: drop anything a pre-§ F201 build left (an HMAC-token
// identity the server no longer accepts). Idempotent.
try {
  migrateLegacyPlayer();
} catch {
  /* private mode */
}

/**
 * The snapshot has to be REFERENTIALLY STABLE or `useSyncExternalStore` spins:
 * `loadPlayer()` parses fresh JSON on every call, so the raw string is the
 * snapshot and the object is cached against it.
 */
let cachedRaw: string | null = null;
let cachedPlayer: StoredPlayer | null = null;

function snapshot(): StoredPlayer | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem("gtw_player");
  } catch {
    raw = null;
  }
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedPlayer = loadPlayer();
  }
  return cachedPlayer;
}

/** Server-side / non-browser render: nobody is playing. */
const serverSnapshot = () => null;

export function usePlayer(): {
  player: StoredPlayer | null;
  setPlayer: (next: StoredPlayer | null) => void;
} {
  const player = useSyncExternalStore(subscribePlayer, snapshot, serverSnapshot);
  const setPlayer = useCallback((next: StoredPlayer | null) => savePlayer(next), []);
  return { player, setPlayer };
}

export default usePlayer;

/** § F201 — every account signed in on this device, as React state. */
let cachedAccountsRaw: string | null = null;
let cachedAccounts: StoredPlayer[] = [];

function accountsSnapshot(): StoredPlayer[] {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem("gtw_accounts");
  } catch {
    raw = null;
  }
  if (raw !== cachedAccountsRaw) {
    cachedAccountsRaw = raw;
    cachedAccounts = loadAccounts();
  }
  return cachedAccounts;
}

const noAccounts: StoredPlayer[] = [];

export function useAccounts(): StoredPlayer[] {
  return useSyncExternalStore(subscribePlayer, accountsSnapshot, () => noAccounts);
}
